export declare namespace InspectionTablesTokenSections {
	interface Wrapper {
		overflowX: string;
		overflowY: string;
		scrollbarGutter: string;
	}

	interface Table {
		minWidth: string;
		theadBackground: string;
		theadColor: string;
		thFontSize: string;
		thFontWeight: string;
		thLineHeight: string;
		thPadding: string;
		theadBorderBottomWidth: string;
		theadBorderBottomColor: string;
		tdPadding: string;
		tdFontSize: string;
		cellBorderColor: string;
		cellVerticalAlign: string;
		cellControlDisplay: string;
		colWidths: {
			createdDate: string;
			activities: string;
			deficiencies: string;
			directions: string;
			deadline: string;
		};
		colWidthsPrevious: {
			createdDate: string;
			deficiencies: string;
			directions: string;
			status: string;
			deadline: string;
			actions: string;
		};
		colWidthsImplementation: {
			deficiency: string;
			deficiencyMinWidth: string;
			deadline: string;
			status: string;
			extended: string;
			action: string;
		};
		rowExpandedBackground: string;
	}

	interface SectionActions {
		display: string;
		flexWrap: string;
		alignItems: string;
		gap: string;
		marginTop: string;
		justifyContent: string;
	}
}

export type InspectionTablesTokens = {
	wrapper: InspectionTablesTokenSections.Wrapper;
	table: InspectionTablesTokenSections.Table;
	sectionActions: InspectionTablesTokenSections.SectionActions;
};

export const wrapper: InspectionTablesTokenSections.Wrapper = {
	overflowX: 'auto',
	overflowY: 'visible',
	/** Reduces layout jump when scrollbars briefly appear (e.g. Prime datepicker panel reparent). */
	scrollbarGutter: 'stable',
};

export const table: InspectionTablesTokenSections.Table = {
	minWidth: '100%',
	/** OKIS_tables thead — same for the detected / previous / implementation deficiency tables */
	theadBackground: '{primary.50}',
	theadColor: '{surface.800}',
	thFontSize: '0.75rem',
	thFontWeight: '500',
	thLineHeight: '1.25rem',
	thPadding: '8px 16px',
	/** Separator under header row only (no top/side borders on th). */
	theadBorderBottomWidth: '1px',
	theadBorderBottomColor: '{surface.200}',
	tdPadding: '8px',
	tdFontSize: '0.875rem',
	cellBorderColor: '{surface.200}',
	cellVerticalAlign: 'top',
	cellControlDisplay: 'block',
	/** Figma OKIS_tables detected thead column widths (px, used with `table-layout: fixed`). */
	colWidths: {
		createdDate: '148px',
		activities: '256px',
		deficiencies: '348px',
		directions: '348px',
		deadline: '204px',
	},
	/** Figma OKIS_tables previous thead column widths (px, used with `table-layout: fixed`). */
	colWidthsPrevious: {
		createdDate: '128px',
		deficiencies: '372px',
		directions: '340px',
		/** 204px: longest option label "Dalinai įgyvendintas" needs ~130px text space;
		 *  available = 204 − 35 (dropdown) − 24 (12px h-pad × 2) = 145px. */
		status: '204px',
		deadline: '128px',
		actions: '157px',
	},
	/**
	 * Sanction step deficiency implementation thead column widths (used with `table-layout: fixed`). The deficiency
	 * column takes the width the fixed columns leave; `deficiencyMinWidth` is the least it keeps.
	 */
	colWidthsImplementation: {
		deficiency: 'auto',
		deficiencyMinWidth: '20rem',
		deadline: '10rem',
		status: '17rem',
		extended: '16rem',
		action: '18rem',
	},
	/** tbody row whose detail row is open below it. */
	rowExpandedBackground: '{surface.50}',
};

/** Bottom actions: Figma `Frame 1171276129` (view) vs `Frame 1171276123` (edit) */
export const sectionActions: InspectionTablesTokenSections.SectionActions = {
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	gap: '12px',
	marginTop: '16px',
	justifyContent: 'flex-end',
};

/**
 * Inspection deficiency-step data tables (Figma: `Frame 1000006679` → `OKIS_tables`, thead layer `thead`).
 * Thead (Figma **11832:27131** and shared OKIS_tables pattern): no cell borders except **bottom** edge vs tbody.
 */
export default {
	wrapper,
	table,
	sectionActions,
} satisfies InspectionTablesTokens;
